/**
 * Country platform status + computed phase system.
 *
 * DB stores `platformStatus`: "hidden" | "coming_soon" | "open"
 *
 * For "open" countries, a phase is computed from data thresholds:
 *   seeding   → amber (scraped data only, no claimed providers)
 *   directory → sky/blue (≥1 claimed provider, search works, booking where available)
 *   booking   → green/teal (≥50 providers with schedules, full booking experience)
 *
 * Moved to packages/ui in Phase 2 country-bootstrap work so apps/app can
 * reuse the same status taxonomy as apps/web without cross-app imports.
 */

export type PlatformStatus = "hidden" | "coming_soon" | "open";
export type CountryPhase = "seeding" | "directory" | "booking";
export type CountryLevel = "hidden" | "coming_soon" | "seeding" | "directory" | "booking";

export function getCountryLevel(country: {
  platformStatus: string;
  totalFacilities: number;
  totalProviders: number;
  totalClaimed?: number;
  providersWithSchedule?: number;
}): CountryLevel {
  if (country.platformStatus === "hidden") return "hidden";
  if (country.platformStatus === "coming_soon") return "coming_soon";

  const claimed = country.totalClaimed ?? 0;
  const withSchedule = country.providersWithSchedule ?? 0;

  if (withSchedule >= 50) return "booking";
  if (claimed >= 1) return "directory";
  return "seeding";
}

export function mapLegacyLevel(level: string): CountryLevel {
  switch (level) {
    case "active":
      return "booking";
    case "connected":
      return "directory";
    case "open":
      return "seeding";
    default:
      return level as CountryLevel;
  }
}

export const LEVEL_CONFIG = {
  hidden: {
    label: "Hidden",
    labelLong: "Hidden market",
    color: "border-zinc-300 bg-zinc-50 text-zinc-600",
    dot: "bg-zinc-300",
    mapColor: "#d4d4d8",
  },
  coming_soon: {
    label: "Coming Soon",
    labelLong: "Launching soon",
    color: "border-purple-300 bg-purple-50 text-purple-700",
    dot: "bg-purple-400",
    mapColor: "#c084fc",
  },
  seeding: {
    label: "Seeding",
    labelLong: "Registrations open",
    color: "border-amber-300 bg-amber-50 text-amber-700",
    dot: "bg-amber-400",
    mapColor: "#fbbf24",
  },
  directory: {
    label: "Directory",
    labelLong: "Search available",
    color: "border-sky-300 bg-sky-50 text-sky-700",
    dot: "bg-sky-400",
    mapColor: "#38bdf8",
  },
  booking: {
    label: "Booking",
    labelLong: "Online booking active",
    color: "border-emerald-300 bg-emerald-50 text-emerald-700",
    dot: "bg-emerald-500",
    mapColor: "#10b981",
  },
} as const;
